import get from 'lodash/get';
import has from 'lodash/has';
import React from 'react';
import { ForgeList, ForgeIcon, ForgeInlineMessage } from '@tylertech/forge-react';

import ProcessDisconveryDescriptorLinks from './ProcessDiscoveryDescriptorLinks';
import { useProcessDiscoveryResults } from 'common/visualizations/views/agGridReact/customHooks/useProcessDiscoveryResults';
import {
  ApplicationDescriptor,
  ContextParams,
} from 'common/visualizations/views/agGridReact/helpers/processDiscoveryHelper';
import { isEmpty } from 'lodash';
import { I18n } from 'common/i18n';

const scope = 'shared.visualizations.agTable.actions.errors';

export interface ContentProps {
  primaryKeyValue: string | number;
  contextParams: ContextParams;
  onLinkClick: (url: string) => void;
  newTableActions: string[];
}

export interface ProcessDescriptor {
  id: string;
  name: string;
}

export interface ProcessDiscoveryResults {
  errorCode?: number;
  applicationDescriptors?: ApplicationDescriptor[];
  processDescriptors?: ProcessDescriptor[];
}

const ProcessDiscoveryContent: React.FC<ContentProps> = ({
  primaryKeyValue,
  contextParams,
  newTableActions,
  onLinkClick,
}) => {
  const {
    loading,
    processDiscoveryResults,
  }: { loading: boolean; processDiscoveryResults: ProcessDiscoveryResults } = useProcessDiscoveryResults(
    primaryKeyValue,
    contextParams,
  );

  const { applicationDescriptors = [] } = get(processDiscoveryResults, '[0]', {});
  if (loading) {
    return <div className="spinner-default" data-testid="process-discovery-spinner"></div>;
  }

  if (has(processDiscoveryResults, 'errorCode')) {
    let label;
    let icon;
    switch (processDiscoveryResults.errorCode) {
      case 401:
        label = 'unauthorized';
        icon = 'info';
        break;
      case 404:
        label = 'not_found';
        icon = 'error';
        break;
      case 400:
        label = 'no_results';
        icon = 'info';
        break;
      default:
        label = 'unexpected';
        icon = 'info';
        break;
    }

    return (
      <ForgeInlineMessage theme="error">
        <ForgeIcon slot="icon" name={icon}></ForgeIcon>
        <p>{I18n.t(label, { scope })}</p>
      </ForgeInlineMessage>
    );
  }

  if (isEmpty(processDiscoveryResults)) {
    return (
      <ForgeInlineMessage theme="error">
        <ForgeIcon slot="icon" name="info"></ForgeIcon>
        <p data-testid="process-discovery-error-message">{I18n.t('no_results', { scope })}</p>
      </ForgeInlineMessage>
    );
  }

  return (
    <div className="links">
      <ForgeList>
        <ProcessDisconveryDescriptorLinks
          descriptors={applicationDescriptors}
          onLinkClick={onLinkClick}
          newTableActions={newTableActions}
        />
      </ForgeList>
    </div>
  );
};

export default ProcessDiscoveryContent;
